'use client';

import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Minus, Binoculars, ChevronDown, ChevronUp, Share2 } from 'lucide-react';
import { useTwitterProfileAnalysis, getAnalysisTimeAgo, isAnalysisStale } from '@/hooks/useTwitterProfileAnalysis';
import { TwitterProfileAnalysis } from '@/types/twitter-analysis';
import ReconIntelShareModal from './ReconIntelShareModal';

/**
 * Format text to highlight tokens and numbers
 */
function formatScannableText(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const items: Array<{ index: number; length: number; element: React.ReactNode }> = [];
  
  // Find token symbols ($ followed by 2+ uppercase letters)
  const tokenRegex = /\$[A-Z]{2,}/g;
  let match;
  while ((match = tokenRegex.exec(text)) !== null) {
    items.push({
      index: match.index,
      length: match[0].length,
      element: <strong key={`token-${match.index}`} className="font-semibold text-amber-500">{match[0]}</strong>
    });
  }
  
  // Find @mentions - make them clickable links to Twitter
  const mentionRegex = /@[A-Za-z0-9_]+/g;
  while ((match = mentionRegex.exec(text)) !== null) {
    const handle = match[0].slice(1); // Remove @ prefix
    items.push({
      index: match.index,
      length: match[0].length,
      element: (
        <a 
          key={`mention-${match.index}`} 
          href={`https://twitter.com/${handle}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-sky-400 hover:text-sky-300 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {match[0]}
        </a>
      )
    });
  }
  
  // Find dollar amounts and large numbers (but NOT dates or ranges)
  // Match: $2M, $500K, 49.4K, 100M+, but avoid date-like patterns
  const moneyRegex = /\$[\d,]+\.?\d*[KMB]?/gi;
  const moneyMatches = text.matchAll(moneyRegex);
  for (const numMatch of moneyMatches) {
    items.push({
      index: numMatch.index!,
      length: numMatch[0].length,
      element: (
        <span key={`money-${numMatch.index}`} className="font-semibold text-white">
          {numMatch[0]}
        </span>
      )
    });
  }
  
  // Find large numbers with K/M/B suffix (followers, etc)
  const bigNumRegex = /\b[\d,]+\.?\d*[KMB]\b/gi;
  const bigNumMatches = text.matchAll(bigNumRegex);
  for (const numMatch of bigNumMatches) {
    items.push({
      index: numMatch.index!,
      length: numMatch[0].length,
      element: (
        <span key={`bignum-${numMatch.index}`} className="font-semibold text-white">
          {numMatch[0]}
        </span>
      )
    });
  }
  
  // Find percentage changes (these get color)
  const percentRegex = /[+-]?\d+\.?\d*%/gi;
  const percentMatches = text.matchAll(percentRegex);
  for (const numMatch of percentMatches) {
    const numText = numMatch[0];
    const isNegative = numText.startsWith('-');
    const isPositive = numText.startsWith('+');
    
    items.push({
      index: numMatch.index!,
      length: numText.length,
      element: (
        <span 
          key={`pct-${numMatch.index}`} 
          className={`font-semibold ${
            isNegative ? 'text-red-400' : 
            isPositive ? 'text-green-400' : 
            'text-white'
          }`}
        >
          {numText}
        </span>
      )
    });
  }
  
  // Sort and remove overlaps
  items.sort((a, b) => a.index - b.index);
  const filteredItems: typeof items = [];
  let lastEnd = 0;
  for (const item of items) {
    if (item.index >= lastEnd) {
      filteredItems.push(item);
      lastEnd = item.index + item.length;
    }
  }
  
  // Build output
  filteredItems.forEach((item) => {
    if (item.index > lastIndex) {
      parts.push(text.slice(lastIndex, item.index));
    }
    parts.push(item.element);
    lastIndex = item.index + item.length;
  });
  
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  return parts.length > 0 ? parts : text;
}

/**
 * Format text with light mode support - highlights tokens, numbers, and @mentions
 */
function formatScannableTextLightMode(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const items: Array<{ index: number; length: number; element: React.ReactNode }> = [];
  
  // Find token symbols ($ followed by 2+ uppercase letters)
  const tokenRegex = /\$[A-Z]{2,}/g;
  let match;
  while ((match = tokenRegex.exec(text)) !== null) {
    items.push({
      index: match.index,
      length: match[0].length,
      element: <strong key={`token-${match.index}`} className="font-semibold text-amber-600 dark:text-amber-500">{match[0]}</strong>
    });
  }
  
  // Find @mentions
  const mentionRegex = /@[A-Za-z0-9_]+/g;
  while ((match = mentionRegex.exec(text)) !== null) {
    const handle = match[0].slice(1);
    items.push({
      index: match.index,
      length: match[0].length,
      element: (
        <a 
          key={`mention-${match.index}`} 
          href={`https://twitter.com/${handle}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {match[0]}
        </a>
      )
    });
  }
  
  // Find dollar amounts and large numbers
  const moneyRegex = /\$[\d,]+\.?\d*[KMB]?/gi;
  const moneyMatches = text.matchAll(moneyRegex);
  for (const numMatch of moneyMatches) {
    items.push({
      index: numMatch.index!,
      length: numMatch[0].length,
      element: (
        <span key={`money-${numMatch.index}`} className="font-semibold text-neutral-900 dark:text-white">
          {numMatch[0]}
        </span>
      )
    });
  }
  
  // Find large numbers with K/M/B suffix
  const bigNumRegex = /\b[\d,]+\.?\d*[KMB]\b/gi;
  const bigNumMatches = text.matchAll(bigNumRegex);
  for (const numMatch of bigNumMatches) {
    items.push({
      index: numMatch.index!,
      length: numMatch[0].length,
      element: (
        <span key={`bignum-${numMatch.index}`} className="font-semibold text-neutral-900 dark:text-white">
          {numMatch[0]}
        </span>
      )
    });
  }
  
  // Find percentage changes (these get color)
  const percentRegex = /[+-]?\d+\.?\d*%/gi;
  const percentMatches = text.matchAll(percentRegex);
  for (const numMatch of percentMatches) {
    const numText = numMatch[0];
    const isNegative = numText.startsWith('-');
    const isPositive = numText.startsWith('+');
    
    items.push({
      index: numMatch.index!,
      length: numText.length,
      element: (
        <span 
          key={`pct-${numMatch.index}`} 
          className={`font-semibold ${
            isNegative ? 'text-red-600 dark:text-red-400' : 
            isPositive ? 'text-green-600 dark:text-green-400' : 
            'text-neutral-900 dark:text-white'
          }`}
        >
          {numText}
        </span>
      )
    });
  }
  
  // Sort and remove overlaps
  items.sort((a, b) => a.index - b.index);
  const filteredItems: typeof items = [];
  let lastEnd = 0;
  for (const item of items) {
    if (item.index >= lastEnd) {
      filteredItems.push(item);
      lastEnd = item.index + item.length;
    }
  }
  
  // Build output
  filteredItems.forEach((item) => {
    if (item.index > lastIndex) {
      parts.push(text.slice(lastIndex, item.index));
    }
    parts.push(item.element);
    lastIndex = item.index + item.length;
  });
  
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  return parts.length > 0 ? parts : text;
}

/**
 * Sentiment Badge with light mode support
 */
function SentimentBadgeLightMode({ sentiment }: { sentiment: string }) {
  const config: Record<string, { bg: string; text: string; icon: React.ReactNode; label: string }> = {
    bullish: { 
      bg: 'bg-green-100 dark:bg-green-950/50 border-green-300 dark:border-green-900/50', 
      text: 'text-green-700 dark:text-green-400',
      icon: <TrendingUp className="w-3 h-3" />,
      label: 'Bullish'
    },
    bearish: { 
      bg: 'bg-red-100 dark:bg-red-950/50 border-red-300 dark:border-red-900/50', 
      text: 'text-red-700 dark:text-red-400',
      icon: <TrendingDown className="w-3 h-3" />,
      label: 'Bearish'
    },
    informational: { 
      bg: 'bg-blue-100 dark:bg-blue-950/50 border-blue-300 dark:border-blue-900/50', 
      text: 'text-blue-700 dark:text-blue-400',
      icon: <Binoculars className="w-3 h-3" />,
      label: 'Info'
    },
    neutral: { 
      bg: 'bg-neutral-100 dark:bg-neutral-800/50 border-neutral-300 dark:border-neutral-700/50', 
      text: 'text-neutral-600 dark:text-neutral-400',
      icon: <Minus className="w-3 h-3" />,
      label: 'Neutral'
    }
  };
  
  const { bg, text, icon, label } = config[sentiment] || config.neutral;
  
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border font-medium ${bg} ${text}`}>
      {icon}
      {label}
    </span>
  );
}

interface ProfilePulseProps {
  handleId: string;
  className?: string;
  variant?: 'default' | 'compact' | 'embedded';
  showWhenEmpty?: boolean;
}

/**
 * ProfilePulse - Displays AI analysis for a single Twitter profile
 * Clean, minimal design with expandable events section
 */
export function ProfilePulse({ handleId, className = '', variant = 'default', showWhenEmpty = false }: ProfilePulseProps) {
  const { analysis, loading, error } = useTwitterProfileAnalysis(handleId);
  const [isExpanded, setIsExpanded] = useState(false);

  // Embedded variant uses different loading/error styling (no card wrapper)
  const isEmbedded = variant === 'embedded';

  // Loading state
  if (loading) {
    return (
      <div className={`${isEmbedded ? 'px-5 py-4' : 'bg-white dark:bg-neutral-900/80 rounded-xl border border-neutral-200 dark:border-neutral-800 p-4'} animate-pulse ${className}`}>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded w-40" />
            <div className="h-3 bg-neutral-100 dark:bg-neutral-800 rounded w-full" />
          </div>
        </div>
      </div>
    );
  }

  // No analysis or error
  if (error || !analysis) {
    if (showWhenEmpty) {
      return (
        <div className={`${isEmbedded ? 'px-5 py-4' : 'bg-neutral-50 dark:bg-neutral-900/50 rounded-xl border border-dashed border-neutral-300 dark:border-neutral-700 p-4'} ${className}`}>
          <div className="flex items-center gap-2 text-neutral-500 text-sm">
            <Binoculars className="w-4 h-4" />
            <span>AI analysis not available for @{handleId}</span>
          </div>
        </div>
      );
    }
    return null;
  }

  // Check if stale
  const isStale = isAnalysisStale(analysis, 48);
  if (isStale && !showWhenEmpty) {
    return null;
  }

  // Compact variant
  if (variant === 'compact') {
    return <ProfilePulseCompact analysis={analysis} className={className} />;
  }

  // Embedded variant (no card wrapper, integrates into parent)
  if (variant === 'embedded') {
    return <ProfilePulseEmbedded analysis={analysis} className={className} isExpanded={isExpanded} setIsExpanded={setIsExpanded} />;
  }

  // Full variant
  return <ProfilePulseFull analysis={analysis} className={className} isExpanded={isExpanded} setIsExpanded={setIsExpanded} />;
}

/**
 * Compact version for inline display
 * Updated with light mode support and cleaner design (no avatar)
 */
function ProfilePulseCompact({ analysis, className }: { analysis: TwitterProfileAnalysis; className: string }) {
  return (
    <div className={`relative bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-3 overflow-hidden ${className}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <Binoculars size={12} className="text-scout-gold" />
          <span className="font-mono text-[10px] font-bold text-neutral-900 dark:text-white uppercase tracking-wider">RECON Intel</span>
          <SentimentBadgeLightMode sentiment={analysis.sentiment} />
        </div>
        <a
          href={`https://twitter.com/${analysis.username}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-neutral-500 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          @{analysis.username}
        </a>
        <p className="font-mono text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed mt-1.5">
          {formatScannableTextLightMode(analysis.summary)}
        </p>
      </div>
    </div>
  );
}

/**
 * Embedded version - no card wrapper, integrates into parent container
 * Updated with light mode support and cleaner design (no avatar)
 */
function ProfilePulseEmbedded({ 
  analysis, 
  className,
  isExpanded,
  setIsExpanded 
}: { 
  analysis: TwitterProfileAnalysis; 
  className: string;
  isExpanded: boolean;
  setIsExpanded: (v: boolean) => void;
}) {
  const [showShareModal, setShowShareModal] = useState(false);

  // Check if there's expandable content
  const hasTopics = analysis.keyTopics && analysis.keyTopics.length > 0;
  const hasInsight = !!analysis.actionableInsight;
  const hasEvents = analysis.upcomingEvents && analysis.upcomingEvents.length > 0;
  const hasExpandableContent = hasTopics || hasInsight || hasEvents;

  return (
    <>
      <div className={`${className}`}>
        {/* Header */}
        <div className="px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            {/* Left: Title + Handle */}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Binoculars size={12} className="text-scout-gold" />
                <span className="font-mono text-[10px] font-bold text-neutral-900 dark:text-white uppercase tracking-wider">RECON Intel</span>
                <SentimentBadgeLightMode sentiment={analysis.sentiment} />
              </div>
              <a
                href={`https://twitter.com/${analysis.username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-neutral-500 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                @{analysis.username}
              </a>
            </div>
            
            {/* Right: Meta info */}
            <div className="flex items-center gap-2 text-xs text-neutral-500 flex-shrink-0">
              <span>{getAnalysisTimeAgo(analysis.generatedAt)}</span>
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-1 px-1.5 py-1 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-400 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
                title="Share this take"
              >
                <Share2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="px-5 pb-4">
          <p className="font-mono text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed">
            {formatScannableTextLightMode(analysis.summary)}
          </p>
        </div>

        {/* Expandable Section */}
        {hasExpandableContent && (
          <>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full px-5 py-2 flex items-center justify-center gap-1.5 text-xs font-mono text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800/50 transition-colors border-t border-neutral-200 dark:border-neutral-800"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-3.5 h-3.5" />
                  Hide details
                </>
              ) : (
                <>
                  <ChevronDown className="w-3.5 h-3.5" />
                  {hasEvents 
                    ? `${analysis.upcomingEvents!.length} event${analysis.upcomingEvents!.length > 1 ? 's' : ''} & more`
                    : 'Show more'
                  }
                </>
              )}
            </button>

            {isExpanded && (
              <div className="px-5 pb-4 space-y-3">
                {/* Topics */}
                {hasTopics && (
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.keyTopics!.slice(0, 4).map((topic, i) => (
                      <span 
                        key={i}
                        className="text-[11px] font-mono px-2.5 py-1 bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 rounded-full"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                )}

                {/* Actionable Insight */}
                {hasInsight && (
                  <div className="pl-3 border-l-2 border-emerald-500 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-950/20 rounded-r-lg py-2 pr-3">
                    <p className="font-mono text-xs text-neutral-600 dark:text-neutral-400 italic leading-relaxed">
                      ✅ {analysis.actionableInsight}
                    </p>
                  </div>
                )}

                {/* Upcoming Events */}
                {hasEvents && (
                  <div className="space-y-2">
                    {analysis.upcomingEvents!.map((event, i) => (
                      <div 
                        key={i}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 text-xs font-mono px-3 py-2.5 bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800/30 text-sky-700 dark:text-sky-200 rounded-lg"
                      >
                        <span className="leading-snug">{event.event}</span>
                        <span className="text-sky-600 dark:text-sky-400/70 text-xs font-medium flex-shrink-0">{event.timeframe}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Share Modal */}
      <ReconIntelShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        analysis={analysis}
        entityName={analysis.username}
      />
    </>
  );
}

/**
 * Get sentiment config for styling
 */
function getSentimentConfig(sentiment: string) {
  switch (sentiment) {
    case 'bullish':
      return {
        borderColor: 'border-green-500',
        shadowColor: 'shadow-green-500/20',
        glowBg: 'bg-gradient-to-br from-green-500/10 via-transparent to-transparent',
      };
    case 'bearish':
      return {
        borderColor: 'border-red-500',
        shadowColor: 'shadow-red-500/20',
        glowBg: 'bg-gradient-to-br from-red-500/10 via-transparent to-transparent',
      };
    case 'informational':
      return {
        borderColor: 'border-blue-500',
        shadowColor: 'shadow-blue-500/20',
        glowBg: 'bg-gradient-to-br from-blue-500/10 via-transparent to-transparent',
      };
    default:
      return {
        borderColor: 'border-neutral-500',
        shadowColor: 'shadow-neutral-500/20',
        glowBg: 'bg-gradient-to-br from-neutral-500/5 via-transparent to-transparent',
      };
  }
}

/**
 * Full version with expandable details section
 * Shows only summary by default, topics + insight + events are expandable
 * Updated with light mode support and cleaner design (no avatar)
 */
function ProfilePulseFull({ 
  analysis, 
  className, 
  isExpanded, 
  setIsExpanded
}: { 
  analysis: TwitterProfileAnalysis; 
  className: string;
  isExpanded: boolean;
  setIsExpanded: (v: boolean) => void;
}) {
  const [showShareModal, setShowShareModal] = useState(false);

  // Check if there's expandable content
  const hasTopics = analysis.keyTopics && analysis.keyTopics.length > 0;
  const hasInsight = !!analysis.actionableInsight;
  const hasEvents = analysis.upcomingEvents && analysis.upcomingEvents.length > 0;
  const hasExpandableContent = hasTopics || hasInsight || hasEvents;

  return (
    <>
    <div className={`relative bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="relative px-4 py-3 sm:px-5">
        <div className="flex items-start justify-between gap-3">
          {/* Left: Title + Handle */}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Binoculars size={12} className="text-scout-gold" />
              <span className="font-mono text-[10px] font-bold text-neutral-900 dark:text-white uppercase tracking-wider">RECON Intel</span>
              <SentimentBadgeLightMode sentiment={analysis.sentiment} />
            </div>
            <a
              href={`https://twitter.com/${analysis.username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-neutral-500 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              @{analysis.username}
            </a>
          </div>
          
          {/* Right: Meta info - compact */}
          <div className="flex items-center gap-2 text-xs text-neutral-500 flex-shrink-0">
            <span>{getAnalysisTimeAgo(analysis.generatedAt)}</span>
            <button
              onClick={() => setShowShareModal(true)}
              className="flex items-center gap-1 px-1.5 py-1 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-400 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
              title="Share this take"
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Summary - Always Visible */}
      <div className="relative px-4 sm:px-5 pb-3">
        <p className="font-mono text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed">
          {formatScannableTextLightMode(analysis.summary)}
        </p>
      </div>

      {/* Expandable Section: Topics + Insight + Events */}
      {hasExpandableContent && (
        <>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="relative w-full px-4 sm:px-5 py-2 flex items-center justify-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors border-t border-neutral-200 dark:border-neutral-800"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3.5 h-3.5" />
                Hide details
              </>
            ) : (
              <>
                <ChevronDown className="w-3.5 h-3.5" />
                {hasEvents 
                  ? `${analysis.upcomingEvents!.length} event${analysis.upcomingEvents!.length > 1 ? 's' : ''} & more`
                  : 'Show more'
                }
              </>
            )}
          </button>

          {isExpanded && (
            <div className="relative px-4 sm:px-5 pb-4 space-y-3">
              {/* Topics */}
              {hasTopics && (
                <div className="flex flex-wrap gap-1.5">
                  {analysis.keyTopics!.slice(0, 4).map((topic, i) => (
                    <span 
                      key={i}
                      className="text-[11px] px-2.5 py-1 bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 rounded-full"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              )}

              {/* Actionable Insight */}
              {hasInsight && (
                <div className="pl-3 border-l-2 border-emerald-500 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-950/20 rounded-r-lg py-2 pr-3">
                  <p className="font-mono text-xs text-neutral-600 dark:text-neutral-400 italic leading-relaxed">
                    ✅ {analysis.actionableInsight}
                  </p>
                </div>
              )}

              {/* Upcoming Events */}
              {hasEvents && (
                <div className="space-y-2">
                  {analysis.upcomingEvents!.map((event, i) => (
                    <div 
                      key={i}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 text-xs px-3 py-2.5 bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800/30 text-sky-700 dark:text-sky-200 rounded-lg"
                    >
                      <span className="leading-snug">{event.event}</span>
                      <span className="text-sky-600 dark:text-sky-400/70 text-xs font-medium flex-shrink-0">{event.timeframe}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>

    {/* Share Modal */}
    <ReconIntelShareModal
      isOpen={showShareModal}
      onClose={() => setShowShareModal(false)}
      analysis={analysis}
      entityName={analysis.username}
    />
    </>
  );
}

/**
 * Simple sentiment icon (no text)
 */
function SentimentIcon({ sentiment }: { sentiment: string }) {
  switch (sentiment) {
    case 'bullish':
      return <TrendingUp className="w-3.5 h-3.5 text-green-500" />;
    case 'bearish':
      return <TrendingDown className="w-3.5 h-3.5 text-red-500" />;
    default:
      return <Minus className="w-3.5 h-3.5 text-neutral-500" />;
  }
}

/**
 * Sentiment Badge - Shows what this news means for the Abstract ecosystem
 * NOT whether the account "likes" Abstract - but whether the NEWS is good/bad for ecosystem
 */
function SentimentBadge({ sentiment }: { sentiment: string }) {
  const config: Record<string, { bg: string; text: string; icon: React.ReactNode; label: string; tooltip: string }> = {
    bullish: { 
      bg: 'bg-green-950/50 border-green-900/50', 
      text: 'text-green-400',
      icon: <TrendingUp className="w-3 h-3" />,
      label: 'Bullish',
      tooltip: 'Good news for Abstract ecosystem (launches, milestones, growth)'
    },
    bearish: { 
      bg: 'bg-red-950/50 border-red-900/50', 
      text: 'text-red-400',
      icon: <TrendingDown className="w-3 h-3" />,
      label: 'Bearish',
      tooltip: 'Concerning news for ecosystem (delays, issues, setbacks)'
    },
    informational: { 
      bg: 'bg-blue-950/50 border-blue-900/50', 
      text: 'text-blue-400',
      icon: <Binoculars className="w-3 h-3" />,
      label: 'Info',
      tooltip: 'News reporting - no clear positive/negative signal'
    },
    neutral: { 
      bg: 'bg-neutral-800/50 border-neutral-700/50', 
      text: 'text-neutral-400',
      icon: <Minus className="w-3 h-3" />,
      label: 'Neutral',
      tooltip: 'Routine updates - no significant ecosystem impact'
    }
  };
  
  const { bg, text, icon, label, tooltip } = config[sentiment] || config.neutral;
  
  return (
    <span 
      className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border font-medium ${bg} ${text}`}
      title={tooltip}
    >
      {icon}
      {label}
    </span>
  );
}

export default ProfilePulse;
